/* eslint-disable react/jsx-no-constructed-context-values */
import type { ReactElement, ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Box } from "@amzn/awsui-components-react";
import ErrorBoundary from "@amzn/awsui-components-react/polaris/error-boundary";
import Homepage from "./home";
import Dashboard from "./dashboard/Dashboard";
import OMSDashboard from "./dashboard/OMSDashboard";
import RoyaltyReconDashboard from "./dashboard/RoyaltyReconDashboard";
import OrderRedrive from "./redrive/OrderRedrive";
import PaymentRedrive from "./redrive/PaymentRedrive";
import DlqRedrive from "./dlq/DlqRedrive";
import PsnsRedrive from "./psns-redrive/PsnsRedrive";
import { UatPortal } from "./uat/UatPortal";
import UatProjectPage from "./uat/UatProjectPage";
import { ChatUatPortal } from "./chatuat/ChatUatPortal";
import AsanaOAuthCallbackPage from "./chatuat/AsanaOAuthCallbackPage";
import { AnalyticsProvider } from "./dashboard/AnalyticsProvider";
import InvestigationPage from "./investigations/InvestigationPage";
import InvestigationsPage from "./investigations/InvestigationsPage";
import { INVESTIGATION_SECTIONS } from "./investigations/investigationUtils";
import usePermissions from "../hooks/usePermissions";
import { ThemeProvider } from "../contexes/DarkModeContext";

// Redirects to `pathname` while preserving the original query string so
// bookmarked links with filters/views/stage params keep working.
function RedirectPreservingQuery({ pathname }: { pathname: string }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname, search }} replace />;
}

// One bad server value (e.g. a stored URL Cloudscape refuses) must not white-screen a case for
// every viewer: the investigation routes render a refreshable error instead.
function ProjectsRoute({ children }: { children: ReactElement }) {
  const { canRedrive, isLoading } = usePermissions();
  if (isLoading) {
    return <Box padding="l">Checking UAT Projects access…</Box>;
  }
  return canRedrive ? children : <Navigate to="/" replace />;
}

function InvestigationsErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      errorBoundaryId="investigations"
      i18nStrings={{
        headerText: "Unexpected error, investigation failed to display",
        descriptionText: "Refresh the page to try again.",
        refreshActionText: "Refresh page",
      }}
      onError={({ error, errorInfo }) =>
        console.error("Error rendering investigations: ", error, errorInfo)
      }
    >
      {children}
    </ErrorBoundary>
  );
}

export default function App() {
  const queryClient = new QueryClient();

  return (
    <AnalyticsProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <Routes>
            <Route path="/" element={<Homepage />} />
            <Route path="/find" element={<Homepage />} />
            <Route path="/saved" element={<Homepage />} />
            <Route
              path="/projects"
              element={
                <ProjectsRoute>
                  <Homepage />
                </ProjectsRoute>
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                <ProjectsRoute>
                  <UatProjectPage />
                </ProjectsRoute>
              }
            />
            <Route path="/dashboard/dlq" element={<Dashboard />} />
            <Route
              path="/dashboard/dlq/investigations"
              element={
                <InvestigationsErrorBoundary>
                  <InvestigationsPage />
                </InvestigationsErrorBoundary>
              }
            />
            {/* Section pages are static segments, so they win over the investigation-id route. */}
            {INVESTIGATION_SECTIONS.filter((s) => s !== "overview").map(
              (section) => (
                <Route
                  key={section}
                  path={`/dashboard/dlq/investigations/${section}`}
                  element={
                    <InvestigationsErrorBoundary>
                      <InvestigationsPage />
                    </InvestigationsErrorBoundary>
                  }
                />
              )
            )}
            <Route
              path="/dashboard/dlq/investigations/:investigationId"
              element={
                <InvestigationsErrorBoundary>
                  <InvestigationPage />
                </InvestigationsErrorBoundary>
              }
            />
            <Route path="/dashboard/oms" element={<OMSDashboard />} />
            <Route path="/dashboard/oms/:product" element={<OMSDashboard />} />
            <Route
              path="/dashboard/royalty-recon"
              element={<RoyaltyReconDashboard />}
            />
            <Route
              path="/dashboard/royalty-recon/:product"
              element={<RoyaltyReconDashboard />}
            />
            <Route path="/dashboard/uat" element={<UatPortal />} />
            <Route path="/dashboard/uat/chat" element={<ChatUatPortal />} />
            <Route
              path="/dashboard/uat/chat/asana/callback"
              element={<AsanaOAuthCallbackPage />}
            />
            {/* Redirect legacy bookmarked URLs to canonical routes */}
            <Route
              path="/dashboard"
              element={<RedirectPreservingQuery pathname="/dashboard/dlq" />}
            />
            <Route
              path="/uat"
              element={<RedirectPreservingQuery pathname="/dashboard/uat" />}
            />
            <Route path="/redrive" element={<OrderRedrive />} />
            <Route path="/payment-redrive" element={<PaymentRedrive />} />
            <Route path="/dlq-redrive" element={<DlqRedrive />} />
            <Route path="/psns-redrive" element={<PsnsRedrive />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ThemeProvider>
      </QueryClientProvider>
    </AnalyticsProvider>
  );
}
